/**
 * Карандаш кэфа / центов: вбить то, что сейчас на сайте, когда лента отстала.
 *
 * Контора — десятичный кэф. Биржа — центы (вероятность × 100): кэф = 1/p
 * считает `buildRow`. Пустой ввод или крестик снимает замок, снова кадр.
 */

import { useEffect, useRef, useState } from 'react';

import { readOwnQuote } from '../lib/row';

export function OddsEdit({
  kind,
  form,
  locked,
  onLock,
  onUnlock,
  children,
  armed = true,
  live = false,
  clickable = false,
  lead,
  tail,
}: {
  kind: 'odds' | 'cents';
  form: string;
  locked: boolean;
  onLock: (form: string) => void;
  onUnlock: () => void;
  children: React.ReactNode;
  armed?: boolean;
  live?: boolean;
  /** Клик по самому числу, без карандаша и без смены вида в покое. */
  clickable?: boolean;
  lead?: string;
  tail?: string;
}): React.ReactElement {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(form);
  const field = useRef<HTMLInputElement>(null);
  const blurLock = useRef(false);
  // Печатают — не то же, что фокус: курсор стоит в поле и пока человек в окне
  // конторы, а кэф кассы туда идти обязан.
  const typed = useRef(false);

  useEffect(() => {
    if (live) blurLock.current = false;
    typed.current = false;
    setEdit(live);
  }, [live]);

  useEffect(() => {
    if (!edit) {
      setDraft(form);
      return;
    }
    if (live && !typed.current) setDraft(form);
  }, [edit, form, live]);

  useEffect(() => {
    if (!edit || live) return;
    const node = field.current;
    if (node === null) return;
    node.focus();
    node.select();
  }, [edit, live]);

  const commit = (value: string, emptyUnlock: boolean): boolean => {
    const raw = value.trim().replace(/\s/g, '').replace(',', '.');
    if (raw === '') {
      if (emptyUnlock) onUnlock();
      return emptyUnlock;
    }
    const next = readOwnQuote(value, kind);
    if (next === null) return false;
    onLock(next);
    return true;
  };

  const label = kind === 'cents' ? 'цена шара в центах' : 'кэф ноги';
  const title = kind === 'cents' ? 'править цену шара' : 'править кэф';

  const start = (event: React.MouseEvent): void => {
    if (!clickable || edit || live) return;
    event.stopPropagation();
    blurLock.current = true;
    setDraft(form);
    setEdit(true);
  };

  return (
    <span
      className={`nx-amt nx-odd${locked ? ' own' : ''}${edit ? ' edit' : ''}${clickable && !edit ? ' hit' : ''}`}
      onClick={start}
    >
      {edit ? (
        <>
        {clickable && lead ? <span className="nx-amt__lead">{lead}</span> : null}
        <input
          ref={field}
          className="nx-amt__in"
          value={draft}
          inputMode="decimal"
          aria-label={label}
          onChange={(event) => {
            const value = event.target.value;
            typed.current = true;
            setDraft(value);
            commit(value, false);
          }}
          onBlur={() => {
            typed.current = false;
            const value = field.current?.value ?? draft;
            if (!blurLock.current) {
              if (value.trim() === '') onUnlock();
              else setDraft(form);
              return;
            }
            if (!commit(value, true)) setDraft(form);
            setEdit(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.blur();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              typed.current = false;
              setDraft(form);
              if (!live) setEdit(false);
            }
            event.stopPropagation();
          }}
          onClick={(event) => event.stopPropagation()}
        />
        {clickable && tail ? <span className="nx-amt__tail">{tail}</span> : null}
        </>
      ) : (
        children
      )}
      {armed && !live && (
      <button
        type="button"
        className="nx-amt__pen"
        title={edit ? 'готово' : title}
        aria-label={title}
        onClick={(event) => {
          event.stopPropagation();
          if (edit) {
            if (!commit(draft, true)) setDraft(form);
            setEdit(false);
            return;
          }
          setDraft(form);
          blurLock.current = true;
          setEdit(true);
        }}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M11.6 2.6 13.4 4.4 6.2 11.6 4 12l.4-2.2z" />
          <path d="M10.4 3.8 12.2 5.6" />
        </svg>
      </button>
      )}
      {armed && locked && (!edit || live) && (
        <button
          type="button"
          className="nx-amt__off"
          title="снова с ленты"
          aria-label="сбросить свой кэф"
          onClick={(event) => {
            event.stopPropagation();
            onUnlock();
          }}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M4.5 4.5 11.5 11.5M11.5 4.5 4.5 11.5" />
          </svg>
        </button>
      )}
    </span>
  );
}
